import storage from '@shared/js/storage';

const SESSION_INACTIVITY_THRESHOLD_SECONDS = 30 * 60;
const PING_ENDPOINT = '/api/v1/ping';
const PING_TIMEOUT_MS = 10_000;

// Generate a client-side session id using the same mechanism as viewerId.
const generateSessionId = () => Date.now() + Math.random().toString(36).substr(2, 9);

const getPageUrlWithoutQueryOrHash = () => window.location.origin + window.location.pathname;

const normalizeHost = (host) => (host || '').replace(/^www\./i, '').toLowerCase();

// Omit in-arena navigation referrers so referrer stats only capture external traffic.
const getRefererForTelemetry = () => {
	const rawReferer = (window.referer || document.referrer || '').trim();
	if (!rawReferer || !window.arenaDomain) {
		return rawReferer;
	}

	try {
		if (normalizeHost(new URL(rawReferer).hostname) === normalizeHost(window.arenaDomain)) {
			return '';
		}
	} catch {
		// Keep non-URL referer values as-is.
	}

	return rawReferer;
};

const log = (message, level = 'info', context = {}) => {
	const contextSuffix = Object.keys(context).length > 0
		? ` (${Object.entries(context).map(([key, value]) => `${key}=${value}`).join(', ')})`
		: '';
	const formattedMessage = `[PING][${new Date().toLocaleString()}] ${message}${contextSuffix}`;

	if (typeof console[level] === 'function') {
		console[level](formattedMessage);
	} else {
		console.log(formattedMessage);
	}
};

const isSessionExpired = (lastActivityTs) => {
	if (!lastActivityTs) {
		return true;
	}

	const secondsSinceLastActivity = Math.floor(Date.now() / 1000) - lastActivityTs;
	return secondsSinceLastActivity > SESSION_INACTIVITY_THRESHOLD_SECONDS;
};

// Resolve or create the arena telemetry session and persist last-activity updates.
const resolveArenaTelemetrySession = () => {
	const nowSeconds = Math.floor(Date.now() / 1000);
	const existingSession = storage.arenaTelemetrySession;
	const sessionExpired = !existingSession || isSessionExpired(existingSession.lastActivityTs);

	if (sessionExpired) {
		const newSession = {
			sessionId:           generateSessionId(),
			sessionStartTs:      nowSeconds,
			lastActivityTs:      nowSeconds,
			sessionStartPending: true,
		};
		storage.arenaTelemetrySession = newSession;

		log('new session created', 'info', {
			sessionId:      newSession.sessionId,
			sessionStartTs: newSession.sessionStartTs,
		});

		return {
			...newSession,
			sessionStart: true,
		};
	}

	const secondsSinceLastActivity = nowSeconds - existingSession.lastActivityTs;
	const updatedSession = {
		...existingSession,
		lastActivityTs: nowSeconds,
	};
	storage.arenaTelemetrySession = updatedSession;

	log('existing session reused', 'info', {
		sessionId:      updatedSession.sessionId,
		sessionStartTs: updatedSession.sessionStartTs,
		secondsSinceLastActivity,
	});

	return {
		...updatedSession,
		sessionStart: !!updatedSession.sessionStartPending,
	};
};

// Clears the pending session-start marker after a successful ping reports it.
const markSessionStartReported = () => {
	const existingSession = storage.arenaTelemetrySession;
	if (!existingSession?.sessionStartPending) {
		return;
	}

	storage.arenaTelemetrySession = {
		...existingSession,
		sessionStartPending: false,
	};
};

const sendPageViewPing = (payload) => {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

	return fetch(PING_ENDPOINT, {
		method:  'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body:      JSON.stringify(payload),
		keepalive: true,
		signal:    controller.signal,
	})
		.then((response) => {
			if (!response.ok) {
				log('ping failed with status', 'error', { status: response.status });
				return;
			}

			if (payload.sessionStart) {
				markSessionStartReported();
			}
		})
		.catch((error) => {
			log('ping request failed', 'error', { error: error?.message || error });
		})
		.finally(() => {
			clearTimeout(timeoutId);
		});
};

// Track a page view for the current arena page load without blocking app startup.
export const trackArenaPageView = () => {
	try {
		if (window.Cypress) {
			return;
		}

		const session = resolveArenaTelemetrySession();
		const viewerId = storage.viewerId;

		const payload = {
			sessionId:      session.sessionId,
			viewerId,
			sessionStartTs: session.sessionStartTs,
			sessionStart:   session.sessionStart,
			pageUrl:        getPageUrlWithoutQueryOrHash(),
			isSmartTv:      window.isSmartTv || false,
			referer:        getRefererForTelemetry(),
		};

		sendPageViewPing(payload);
	} catch (error) {
		log('failed to track page view', 'error', { error: error?.message || error });
	}
};
